import { useState } from "react";
import { Form, Input, Modal, notification } from "antd";
import { isNil, isNilOrEmpty } from "@salespark/toolkit";
import apiClient from "utils/apiHandler";
import { useTranslation } from "react-i18next";

///validations/vatid/${vatid}
const { Search } = Input;
const { confirm } = Modal;

const VatId = ({ form, country = "PT", required = false }) => {
  const { t } = useTranslation();
  const [isLoadingVatId, setIsLoadingVatId] = useState(false);

  const validateVatId = async (vatid, showBillingDataFound = false) => {
    try {
      if (isNilOrEmpty(vatid)) {
        return { status: true, data: t("billing.vatIdEmpty") };
      }

      setIsLoadingVatId(true);
      const validateResponse = await apiClient.get(`/validations/vatid/${vatid}`);
      setIsLoadingVatId(false);

      if (!validateResponse.status) {
        unconfirmedVatId();
        return { status: false, data: null };
      }

      if ((validateResponse.data?.name || validateResponse.data?.address) && showBillingDataFound === true) {
        let address, city, postal_code;

        if (isNilOrEmpty(validateResponse.data.address) === false) {
          {
            const addressParts = validateResponse.data.address.split("\n");
            address = addressParts[0] || "";
            city = addressParts[1] || "";
            postal_code = addressParts[2] || "";

            if (isNilOrEmpty(postal_code) === false) {
              const postalCodeParts = postal_code.split(" ");
              postal_code = postalCodeParts[0] || postal_code;
            }
          }
        }
        const viesBillingData = {
          billing_name: validateResponse.data.name,
          address: address,
          city: city,
          postal_code: postal_code,
        };

        billingDataFound(viesBillingData);
      }

      return validateResponse;

      // Error handling
    } catch (error) {
      setIsLoadingVatId(false);
      unconfirmedVatId();
      return { status: false, data: null };
    }
  };

  const billingDataFound = (billing) => {
    confirm({
      width: 500,
      title: t("billing.billingDataFoundTitle"),
      content: (
        <>
          {t("billing.billingDataFoundContent")}
          <br />
          <em>{t("billing.billingDataFoundNote")}</em>
        </>
      ),
      okText: t("billing.confirmYes"),
      okType: "danger",
      cancelText: t("billing.confirmNo"),
      onOk() {
        fillBillingData(billing);
      },
    });
  };

  const fillBillingData = (billing) => {
    // Ensure that if any of the billing data values are null or undefined, we set them to empty string before setting form values
    const sanitizedBilling = sanitizeBillingValues(billing);

    form.setFieldsValue(sanitizedBilling);
  };

  const sanitizeBillingValues = (billing) => {
    const sanitized = { ...(billing || {}) };
    Object.keys(sanitized).forEach((key) => {
      if (sanitized[key] === null || sanitized[key] === undefined) {
        sanitized[key] = "";
      }
    });
    return sanitized;
  };

  const unconfirmedVatId = () => {
    confirm({
      title: t("billing.vatIdLabel"),
      content: t("billing.unconfirmedVatIdContent"),
      okText: t("billing.confirmYes"),
      okType: "primary",
      cancelText: t("billing.confirmNo"),
      keyboard: true,
      maskClosable: true,
      onOk() {
        // Show notification that VATID will not be validated
        notification.warning({
          message: t("billing.vatIdNotValidatedMessage"),
          description: t("billing.vatIdNotValidatedDescription"),
        });
      },
      onCancel() {
        clearVatId();
      },
    });
  };
  const clearVatId = () => {
    try {
      form.setFieldsValue({ vatid: "" });
    } catch (e) {}
  };

  return (
    <Form.Item
      name="vatid"
      label={t("billing.vatIdLabel")}
      className="ant-search-item"
      rules={[
        {
          validator: async (_, value) => {
            // If Country isnt Portugal, then dont validate VATID
            if (country?.toUpperCase() !== "PT") return Promise.resolve();

            if (value === undefined || value === "" || value === null) {
              if (required) {
                return Promise.reject(t("billing.vatIdRequired"));
              }
              return Promise.resolve();
            }

            if (value.length > 0 && value.length < 9 && isNaN(value) === false) {
              return Promise.reject(t("billing.vatIdIncomplete"));
            }
            if (value.length > 0 && value.length < 9 && isNaN(value) === true) {
              return Promise.reject(t("billing.vatIdInvalid"));
            }

            const rv = await validateVatId(value);
            if (rv.status) {
              if (rv.data?.status === false) {
                return Promise.reject(t(rv.data.message));
              } else {
                return Promise.resolve(t("billing.vatIdValid"));
              }
            } else {
              return Promise.reject(t("billing.vatIdVerifyFailed"));
            }
          },
        },
      ]}
      hasFeedback
    >
      <Search
        className="vatid"
        placeholder={t("billing.vatIdLabel")}
        loading={isLoadingVatId}
        enterButton
        onSearch={(e) => {
          const vatid = form.getFieldValue("vatid");
          if (isNilOrEmpty(vatid)) return;
          form.validateFields(["vatid"]);
          validateVatId(vatid, true);
        }}
        onBlur={(e) => {
          const vatid = form.getFieldValue("vatid");
          if (isNilOrEmpty(vatid)) return;
          form.validateFields(["vatid"]);
        }}
      />
    </Form.Item>
  );
};

export default VatId;
